/**
 * daV2.jsx — Primitives UI DA V2 (langage "bold editorial" Earniverse).
 *
 * Fondation de la refonte DA V2 : les pages & modales DA V2 COMPOSENT ces primitives,
 * elles ne les réinventent jamais (§11 / §18 / §18 ter).
 *
 * Source visuelle = previews canoniques versionnées dans public/previews/ :
 *   dav2-explore-v6.html · dav2-buyland-C.html · dav2-modals-payment.html · dav2-wallet-connect.html
 *
 * §20 anti-drift : TOUS les littéraux couleur/font/radius/gradient viennent de ./theme
 *   (COLORS, FONTS, RADII, SHADOW, SPACE, GRADIENTS, media). Zéro #hex / rgba() / gradient inline.
 * §12 ter : responsive by design (rem, clamp(), media()) — au moins un breakpoint par primitive de layout.
 * §12 quinquies : bords arrondis cards 1rem / boutons 0.625rem.
 * §12 sexies : croix de fermeture cerclée obligatoire sur les modales (ModalCloseV2, aria-label).
 *
 * Réutilise les boutons existants de daComponents.jsx (CtaButton vert, PrimaryButton magenta,
 * DangerButton, GhostButton) — NON redéfinis ici.
 */
import React from 'react';
import styled from 'styled-components';
import {COLORS, FONTS, RADII, SHADOW, GRADIENTS, media, FONT_SIZE, FONT_WEIGHT} from './theme';

/* ════════════════════════════════════════════════════════════════════════════
 * 1. CARTE — CardV2 + CornerRibbon
 * ════════════════════════════════════════════════════════════════════════════ */

/**
 * CardV2 — carte blanche signature DA V2.
 * Liseré accent en haut, radius card, ombre douce, hover (élévation + bordure accent).
 * Position relative pour accueillir CornerRibbon / TierBadge en overlay.
 */
export const CardV2 = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${COLORS.white};
  border-radius: ${RADII.card};
  border-top: 0.26rem solid ${COLORS.accent};
  box-shadow: ${SHADOW.card};
  overflow: hidden;
  transition: transform 0.18s, box-shadow 0.18s, border-color 0.18s;

  &:hover {
    transform: translateY(-0.3rem);
    box-shadow: ${SHADOW.hover};
    border-top-color: ${COLORS.accentHover};
  }

  ${media.sm`
    &:hover { transform: none; }
  `}
`;

/**
 * CornerRibbon — triangle magenta en coin haut-droit (optionnel sur CardV2).
 * Clip-path triangulaire, dégradé accent.
 */
export const CornerRibbon = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
  width: 2.2rem;
  height: 2.2rem;
  background: ${GRADIENTS.accent};
  clip-path: polygon(100% 0, 100% 100%, 0 0);
  pointer-events: none;
`;

/* ════════════════════════════════════════════════════════════════════════════
 * 2. EYEBROW — sur-titre mono accent
 * ════════════════════════════════════════════════════════════════════════════ */

/**
 * Eyebrow — sur-titre mono, accent, uppercase, letter-spacing large.
 * $bright : variante rose vif (sur fond sombre — hero, modale dark).
 */
export const Eyebrow = styled.span`
  display: block;
  font-family: ${FONTS.mono};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: ${FONT_SIZE.xs};
  color: ${(p) => (p.$bright ? COLORS.accentBright : COLORS.accent)};
`;

/* ════════════════════════════════════════════════════════════════════════════
 * 3. SECTION HEAD — head + title + bar + count
 * ════════════════════════════════════════════════════════════════════════════ */

/** SectionHead — conteneur flex (titre + barre dégradée + compteur). */
export const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 1rem;
  margin: 2.2rem 0 1.3rem;

  ${media.sm`
    gap: 0.6rem;
    margin: 1.6rem 0 1rem;
  `}
`;

/** SectionTitle — DM Sans italic 800. */
export const SectionTitle = styled.h2`
  font-family: ${FONTS.head};
  font-style: italic;
  font-weight: ${FONT_WEIGHT.extrabold};
  letter-spacing: -0.02em;
  font-size: clamp(1.2rem, 3.2vw, 1.5rem);
  color: ${COLORS.ink};
  margin: 0;
`;

/** SectionBar — barre dégradée accent → transparent, occupe l'espace restant. */
export const SectionBar = styled.span`
  flex: 1;
  height: 0.16rem;
  background: ${GRADIENTS.accentFade};
  border-radius: 0.125rem;
`;

/** SectionCount — compteur mono muted. */
export const SectionCount = styled.span`
  flex: 0 0 auto;
  font-family: ${FONTS.mono};
  font-size: 0.76rem;
  color: ${COLORS.muted};
`;

/* ════════════════════════════════════════════════════════════════════════════
 * 4. CHIP V2 — chip biseauté
 * ════════════════════════════════════════════════════════════════════════════ */

/**
 * ChipV2 — chip clip-path biseauté.
 * Défaut : fond accent-soft, bordure accent45, texte ink.
 * $active : fond accent plein, texte blanc.
 * $muted  : fond bg neutre, bordure line (ex: méta secondaire).
 */
export const ChipV2 = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 0.78rem;
  letter-spacing: 0.02em;
  padding: 0.45em 0.9em;
  clip-path: polygon(0.5rem 0, 100% 0, calc(100% - 0.5rem) 100%, 0 100%);
  white-space: nowrap;
  color: ${(p) => (p.$active ? '#fff' : COLORS.ink)};
  background: ${(p) =>
    p.$active ? COLORS.accent : p.$muted ? COLORS.bg : COLORS.accent14};
  border: 0.0625rem solid
    ${(p) => (p.$active ? COLORS.accent : p.$muted ? COLORS.line : COLORS.accent45)};
`;

/* ════════════════════════════════════════════════════════════════════════════
 * 5. HUD BOX — coordonnée (label + valeur)
 * ════════════════════════════════════════════════════════════════════════════ */

/**
 * HudBox — bloc coordonnée avec bordure-gauche accent.
 * Composé : <HudBox><HudLabel>Coord X</HudLabel><HudValue>142</HudValue></HudBox>.
 * Pour aligner plusieurs HUD côte à côte, utiliser HudRow (grille 3 colonnes).
 */
export const HudRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.7rem;

  ${media.sm`
    gap: 0.5rem;
  `}
`;

export const HudBox = styled.div`
  position: relative;
  overflow: hidden;
  background: ${COLORS.white};
  border: 0.0625rem solid ${COLORS.line};
  border-radius: 0.7rem;
  padding: 0.7rem 0.8rem;
  box-shadow: ${SHADOW.card};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0.2rem;
    background: ${COLORS.accent};
  }
`;

export const HudLabel = styled.div`
  font-family: ${FONTS.mono};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.muted};
`;

export const HudValue = styled.div`
  font-family: ${FONTS.mono};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 1.05rem;
  color: ${COLORS.accent};
  margin-top: 0.15rem;
`;

/* ════════════════════════════════════════════════════════════════════════════
 * 6. TIER BADGE — badge doré biseauté
 * ════════════════════════════════════════════════════════════════════════════ */

/**
 * TierBadge — badge doré (tier VVIP…), clip-path biseauté, DM Sans italic 800 uppercase.
 * Position absolute par défaut (overlay sur média/carte) ; surchargeable via style/props.
 */
export const TierBadge = styled.span`
  display: inline-block;
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.extrabold};
  font-style: italic;
  font-size: ${FONT_SIZE.xs};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${COLORS.goldBadgeInk};
  background: ${GRADIENTS.gold};
  padding: 0.32em 1em 0.32em 0.7em;
  clip-path: polygon(0 0, 100% 0, calc(100% - 0.4rem) 100%, 0 100%);
`;

/* ════════════════════════════════════════════════════════════════════════════
 * 7. PRICE ITALIC — prix Roboto italic 900
 * ════════════════════════════════════════════════════════════════════════════ */

/** PriceItalic — prix Roboto (body) italic 900. */
export const PriceItalic = styled.span`
  font-family: ${FONTS.body};
  font-style: italic;
  font-weight: ${FONT_WEIGHT.heavy};
  font-size: clamp(1rem, 2.5vw, 1.05rem);
  letter-spacing: -0.01em;
  color: ${COLORS.ink};
`;

/* ════════════════════════════════════════════════════════════════════════════
 * 8. DIVIDER GRADIENT — séparateur dégradé
 * ════════════════════════════════════════════════════════════════════════════ */

/** DividerGradient — séparateur fin dégradé accent → transparent. */
export const DividerGradient = styled.div`
  height: 0.16rem;
  background: ${GRADIENTS.accentFade};
  border-radius: 0.125rem;
  margin: 1rem 0 1.2rem;
`;

/* ════════════════════════════════════════════════════════════════════════════
 * 9. PILLS — rangée de filtres pill
 * ════════════════════════════════════════════════════════════════════════════ */

/** PillsRow — conteneur flex wrap pour des Pill de filtre. */
export const PillsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  align-items: center;

  ${media.sm`
    gap: 0.4rem;
  `}
`;

/**
 * Pill — filtre pill.
 * Défaut : fond accent-soft + bordure accent45.
 * $active : fond accent plein, texte blanc.
 */
export const Pill = styled.button`
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: 0.78rem;
  letter-spacing: 0.02em;
  border-radius: ${RADII.pill};
  padding: 0.4em 0.95em;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  color: ${(p) => (p.$active ? '#fff' : COLORS.accent)};
  background: ${(p) => (p.$active ? COLORS.accent : COLORS.accent14)};
  border: 0.0625rem solid ${(p) => (p.$active ? COLORS.accent : COLORS.accent45)};

  &:hover {
    background: ${(p) => (p.$active ? COLORS.accentHover : COLORS.accentSoft)};
    border-color: ${COLORS.accent};
  }
`;

/* ════════════════════════════════════════════════════════════════════════════
 * 10. TABS — onglets soulignés magenta
 * ════════════════════════════════════════════════════════════════════════════ */

/** TabsV2 — conteneur flex d'onglets, ligne de base. */
export const TabsV2 = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1.6rem;
  border-bottom: 0.0625rem solid ${COLORS.line};
  overflow-x: auto;

  ${media.sm`
    gap: 1rem;
  `}
`;

/**
 * Tab — onglet avec soulignement magenta quand actif.
 * $active : texte accent + barre soulignée accent.
 * Compteur optionnel via <TabCount> en children.
 */
export const Tab = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 0.1rem 0.85rem;
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  font-family: ${FONTS.head};
  font-weight: ${(p) => (p.$active ? 800 : 600)};
  font-size: 0.92rem;
  color: ${(p) => (p.$active ? COLORS.accent : COLORS.ink)};
  transition: color 0.12s;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -0.0625rem;
    height: 0.16rem;
    border-radius: 0.125rem 0.125rem 0 0;
    background: ${(p) => (p.$active ? COLORS.accent : 'transparent')};
    transition: background 0.12s;
  }

  &:hover {
    color: ${COLORS.accent};
  }
`;

/** TabCount — pastille compteur sur un Tab. */
export const TabCount = styled.span`
  font-family: ${FONTS.mono};
  font-weight: ${FONT_WEIGHT.semibold};
  font-size: ${FONT_SIZE.xs};
  line-height: 1;
  padding: 0.22em 0.5em;
  border-radius: ${RADII.pill};
  color: ${(p) => (p.$active ? '#fff' : COLORS.accent)};
  background: ${(p) => (p.$active ? COLORS.accent : COLORS.accent14)};
`;

/* ════════════════════════════════════════════════════════════════════════════
 * 11. MODALE DA V2 — shell + croix + eyebrow + title + divider
 * ════════════════════════════════════════════════════════════════════════════ */

/**
 * ModalShellV2 — carte modale DA V2 (carte blanche, liseré accent en haut).
 * NOTE a11y : à composer avec le wrapper ModalShell (overlay + focus trap + esc + scroll lock)
 * de ./ModalShell — ModalShellV2 est la *surface* visuelle, pas l'overlay.
 * $width configurable (défaut 30rem).
 */
export const ModalShellV2 = styled.div`
  position: relative;
  z-index: 3;
  width: ${(p) => p.$width || '30rem'};
  max-width: 100%;
  background: ${COLORS.white};
  border-radius: ${RADII.card};
  border-top: 0.26rem solid ${COLORS.accent};
  box-shadow: ${SHADOW.hover};
  padding: 1.6rem;

  ${media.sm`
    padding: 1.2rem;
  `}
`;

/**
 * ModalCloseV2 — croix de fermeture cerclée haut-droite (§12 sexies).
 * Semi-transparente au repos → fond accent au hover. aria-label obligatoire (défaut "Close").
 * Usage : <ModalCloseV2 onClick={onClose} aria-label="Close" />
 */
const ModalCloseV2Btn = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2.2rem;
  height: 2.2rem;
  border-radius: ${RADII.circle};
  background: ${COLORS.subtle};
  border: 0.0625rem solid ${COLORS.subtle};
  color: ${COLORS.ink};
  font-size: ${FONT_SIZE.base};
  line-height: 1;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover {
    background: ${COLORS.accent};
    color: #fff;
    border-color: ${COLORS.accent};
  }
`;
export const ModalCloseV2 = ({ children, 'aria-label': ariaLabel = 'Close', ...rest }) => (
  <ModalCloseV2Btn type="button" aria-label={ariaLabel} {...rest}>
    {children || '✕'}
  </ModalCloseV2Btn>
);

/** ModalEyebrow — sur-titre mono accent dans une modale (alias sémantique d'Eyebrow). */
export const ModalEyebrow = styled(Eyebrow)``;

/** ModalTitle — titre modale DM Sans italic 800 (réserve la place de la croix). */
export const ModalTitle = styled.h3`
  font-family: ${FONTS.head};
  font-style: italic;
  font-weight: ${FONT_WEIGHT.extrabold};
  letter-spacing: -0.02em;
  line-height: 1;
  font-size: clamp(1.5rem, 4vw, 1.9rem);
  color: ${COLORS.ink};
  margin-top: 0.35rem;
  padding-right: 2.4rem;
`;

/** ModalDivider — séparateur dégradé dans une modale (alias sémantique de DividerGradient). */
export const ModalDivider = styled(DividerGradient)``;

/* ════════════════════════════════════════════════════════════════════════════
 * 12. WALLET ROW — ligne wallet (modale connect)
 * ════════════════════════════════════════════════════════════════════════════ */

const WalletRowBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  width: 100%;
  text-align: left;
  padding: 0.8rem 0.95rem;
  border-radius: 0.65rem;
  border: 0.0625rem solid ${COLORS.line};
  background: ${COLORS.white};
  cursor: pointer;
  transition: border-color 0.14s, background 0.14s, transform 0.14s;

  &:hover {
    border-color: ${COLORS.accent};
    background: ${COLORS.accentSoft};
    transform: translateY(-0.0625rem);
  }

  ${media.sm`
    &:hover { transform: none; }
  `}
`;
const WalletRowIco = styled.span`
  flex-shrink: 0;
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 0.55rem;
  display: grid;
  place-items: center;
  overflow: hidden;
  background: ${COLORS.bg};
  border: 0.0625rem solid ${COLORS.line};

  img {
    width: 1.55rem;
    height: 1.55rem;
    object-fit: contain;
  }
`;
const WalletRowName = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;

  .wallet-name {
    font-family: ${FONTS.head};
    font-weight: ${FONT_WEIGHT.bold};
    font-size: 0.96rem;
    color: ${COLORS.ink};
  }
  .wallet-sub {
    font-family: ${FONTS.mono};
    font-size: ${FONT_SIZE.xs};
    letter-spacing: 0.04em;
    color: ${COLORS.muted};
  }
`;
const WalletRowChip = styled.span`
  flex: 0 0 auto;
  font-family: ${FONTS.head};
  font-weight: ${FONT_WEIGHT.bold};
  font-size: ${FONT_SIZE.xs};
  color: ${COLORS.accent};
  background: ${COLORS.accent14};
  border: 0.0625rem solid ${COLORS.accent45};
  border-radius: ${RADII.pill};
  padding: 0.2em 0.65em;
`;
const WalletRowChev = styled.span`
  flex: 0 0 auto;
  color: ${COLORS.accent};
  font-size: 1.05rem;
  opacity: 0.6;
  transition: opacity 0.14s;

  ${WalletRowBtn}:hover & {
    opacity: 1;
  }
`;

/**
 * WalletRow — ligne sélectionnable de wallet (modale connect).
 * Composée : icône + (nom + sous-titre) + chip optionnel + chevron.
 *
 *   <WalletRow
 *     icon={<img src="…" alt="" />}
 *     name="MetaMask"
 *     subtitle="Browser extension"
 *     chip="Recommended"      // optionnel
 *     onClick={connectMetaMask}
 *   />
 */
export const WalletRow = ({ icon, name, subtitle, chip, ...rest }) => (
  <WalletRowBtn type="button" {...rest}>
    {icon != null && <WalletRowIco>{icon}</WalletRowIco>}
    <WalletRowName>
      <span className="wallet-name">{name}</span>
      {subtitle && <span className="wallet-sub">{subtitle}</span>}
    </WalletRowName>
    {chip && <WalletRowChip>{chip}</WalletRowChip>}
    <WalletRowChev aria-hidden="true">›</WalletRowChev>
  </WalletRowBtn>
);

/** WalletList — conteneur vertical de WalletRow. */
export const WalletList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

/* ════════════════════════════════════════════════════════════════════════════
 * 13. FOOTER DARK — pied de page sombre
 * ════════════════════════════════════════════════════════════════════════════ */

/**
 * FooterDark — footer sombre DA V2 (#0a0510), liseré dégradé en haut.
 * Texte clair muted ; <FooterAccent> pour les segments roses.
 */
export const FooterDark = styled.footer`
  background: ${COLORS.inkDeep};
  color: rgba(255, 255, 255, 0.6);
  margin-top: 3rem;
  padding: 2.2rem 1.6rem;
  text-align: center;
  font-family: ${FONTS.body};
  font-size: 0.82rem;
  border-top: 0.3rem solid transparent;
  border-image: ${GRADIENTS.accent} 1;

  ${media.sm`
    padding: 1.8rem 1.1rem;
    margin-top: 2.2rem;
  `}
`;

/** FooterAccent — segment rose vif dans le FooterDark. */
export const FooterAccent = styled.span`
  color: ${COLORS.accentBright};
`;
